import React, { useState, useEffect } from "react";
import { Modal, TextInput, InlineNotification } from "@carbon/react";
import "./addItemModal.scss";
import { useAddCoworker } from "../../hooks/useAddCoworker";
import { useAddChat } from "../../hooks/useAddChat";
import { queryClient } from "../..";
import { useTranslation } from "react-i18next";
import ReactGA from "react-ga4";

const EMOJI_REGEX =
  /(?![©®™])\p{Extended_Pictographic}|\p{Regional_Indicator}|⃣/u;

export default function AddItemModal({
  openModal,
  setOpenModal,
  table,
  data = [],
  guid,
  // onChatCreated,
  // handleCreationStopped,
  // isNewClient = false,
}) {
  const [value, setValue] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [validation, setValidation] = useState({
    invalid: false,
    invalidText: "",
  });
  const [notification, setNotification] = useState({
    visibility: false,
    title: "",
    subtitle: "",
    kind: "",
  });

  const mutationAddCoworker = useAddCoworker();
  const mutationAddChat = useAddChat();

  const { t } = useTranslation();

  useEffect(() => {
    ReactGA.event("screen_view", {
      firebase_screen: `${table}-AddModal`,
      firebase_screen_class: `${table}-DataTable`,
    });
  }, []);

  useEffect(() => {
    const trimmedValue = value.trim();

    if (table === "chat" && EMOJI_REGEX.test(trimmedValue)) {
      setValidation({
        invalid: true,
        invalidText: `${t("main.chat.addModal.validationText.emojiNotAllowed")}`,
      });
    } else if (data.includes(trimmedValue)) {
      setValidation({
        invalid: true,
        invalidText: `${t("main.chat.addModal.validationText.existedValue")}`,
      });
    } else {
      setValidation({ invalid: false, invalidText: "" });
    }
  }, [value, setValidation, data, table, t]);

  const handleAddItem = () => {
    if (submitting || validation.invalid) return;

    setSubmitting(true);

    if (!value.trim()) {
      setValidation({
        invalid: true,
        invalidText: `${t("main.chat.addModal.validationText.noValue")}`,
      });
      return;
    }

    if (table === "coworkers") {
      mutationAddCoworker.mutate(
        { guid: guid, nick: value },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["chat_client_info"] });
            handleCloseModal();
          },
          onError: (error) => {
            setNotification({
              visibility: true,
              title: "authentication.notification.requestError500.title",
              subtitle:
                "authentication.notification.requestError500.serverError",
              kind: "error",
            });
          },
        },
      );
    } else if (table === "chat") {
      mutationAddChat.mutate(
        { guid: guid, name: value },
        {
          onSuccess: (response) => {
            queryClient.invalidateQueries({ queryKey: ["chat_list"] });
            queryClient.invalidateQueries({
              queryKey: ["chat-relations-list"],
            });

            // if (isNewClient) {
            //   const createdChatId = response?.data?.data?.chatid;
            //   if (createdChatId) {
            //     onChatCreated(createdChatId, value.trim());
            //   }
            // }

            handleCloseModal();
          },
          onError: (error) => {
            setNotification({
              visibility: true,
              title: "authentication.notification.requestError500.title",
              subtitle:
                "authentication.notification.requestError500.serverError",
              kind: "error",
            });
          },
        },
      );
    } else return;
  };

  const handleCloseModal = () => {
    // if (isNewClient) {
    //   handleCreationStopped();
    // }
    setSubmitting(false);
    setOpenModal(false);
    setValue("");
  };

  return (
    <Modal
      open={!!openModal}
      onRequestClose={handleCloseModal}
      modalHeading={`${t("main.chat.addModal.title", { context: table })}`}
      primaryButtonText={`${t("main.chat.addModal.add")}`}
      primaryButtonDisabled={submitting || validation.invalid || !value.trim()}
      secondaryButtonText={`${t("main.chat.addModal.cancel")}`}
      onRequestSubmit={handleAddItem}
      onSecondarySubmit={handleCloseModal}
      closeButtonLabel={`${t("main.modalText.close")}`}
    >
      <TextInput
        id="text-input-1"
        labelText=""
        placeholder={`${t("main.chat.addModal.placeholder", {
          context: table,
        })}`}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        required
        invalid={validation.invalid}
        invalidText={validation.invalidText ? validation.invalidText : ""}
      />
      {notification.visibility && (
        <InlineNotification
          title={t(notification.title)}
          subtitle={t(notification.subtitle)}
          kind={notification.kind}
          onClose={() =>
            setNotification({
              visibility: false,
              title: "",
              subtitle: "",
              kind: "",
            })
          }
          lowContrast={true}
          aria-label={t("authentication.notification.closeNotification")}
          className="notification"
        />
      )}
    </Modal>
  );
}
